import React from 'react';
import { motion } from 'framer-motion';
import {
  Users,
  Target,
  TrendingUp,
  Calendar,
  Building2,
  Compass
} from 'lucide-react';

const WhiteGloveOnboardingSlide = () => {
  return (
    <div className="h-full w-full bg-slate-50 flex items-center justify-center p-8 xl:p-12 relative overflow-hidden font-sans">
        {/* Background Gradients */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-100/40 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/4" />
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-indigo-100/40 rounded-full blur-[100px] translate-y-1/2 -translate-x-1/4" />
        <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:32px_32px] opacity-60" />

        <div className="w-full max-w-6xl z-10 flex flex-col h-full justify-center relative">

            <div className="text-center mb-10">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-blue-600 text-xs font-bold uppercase tracking-wide mb-4 border border-blue-100">
                    <Users className="w-3 h-3" />
                    <span>Dedicated Support</span>
                </div>
                <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
                    White glove <span className="text-blue-600">onboarding, consulting & strategy advisory</span>
                </h2>
                <p className="text-lg text-slate-600 max-w-3xl mx-auto leading-relaxed">
                    We don't just give you software and walk away. With 2 weekly syncs per week,
                    we partner with you to not only get you implemented but actually successful with
                    the technology. Our account managers come from DSO backgrounds and serve as your
                    strategic advisors.
                </p>
            </div>

            <div className="grid grid-cols-3 gap-6">

                {/* Card 1: Twice Weekly Syncs */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    className="bg-white p-7 rounded-2xl shadow-sm border border-blue-200 flex flex-col hover:shadow-lg transition-all"
                >
                    <div className="w-12 h-12 bg-blue-50 rounded-xl border border-blue-100 flex items-center justify-center text-blue-600 mb-5">
                        <Calendar className="w-6 h-6" />
                    </div>

                    <h3 className="text-xl font-bold text-slate-900 mb-3">2 weekly syncs</h3>
                    <p className="text-sm text-slate-500 leading-relaxed flex-1">
                        Two syncs per week during onboarding to not only get you implemented,
                        but actually successful with the technology. We review performance,
                        optimize configurations, and ensure every agent is tuned to your practice.
                    </p>

                    <div className="mt-5 bg-blue-50 rounded-xl border border-blue-100 px-4 py-3">
                        <span className="text-xs font-bold text-blue-600">16 sessions included</span>
                        <span className="text-xs text-slate-400 ml-1">over your first 8 weeks</span>
                    </div>
                </motion.div>

                {/* Card 2: DSO Background */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="bg-white p-7 rounded-2xl shadow-sm border border-slate-200 flex flex-col hover:shadow-lg transition-all"
                >
                    <div className="w-12 h-12 bg-indigo-50 rounded-xl border border-indigo-100 flex items-center justify-center text-indigo-600 mb-5">
                        <Building2 className="w-6 h-6" />
                    </div>

                    <h3 className="text-xl font-bold text-slate-900 mb-3">DSO-experienced team</h3>
                    <p className="text-sm text-slate-500 leading-relaxed flex-1">
                        Our account managers come from DSO backgrounds, having advised dozens
                        of offices across the country. They understand practice operations
                        at scale and know what drives results.
                    </p>

                    <div className="mt-5 bg-indigo-50 rounded-xl border border-indigo-100 px-4 py-3">
                        <span className="text-xs font-bold text-indigo-600">Real operational expertise</span>
                        <span className="text-xs text-slate-400 ml-1">not just tech support</span>
                    </div>
                </motion.div>

                {/* Card 3: Strategic Advisors */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="bg-white p-7 rounded-2xl shadow-sm border border-slate-200 flex flex-col hover:shadow-lg transition-all"
                >
                    <div className="w-12 h-12 bg-violet-50 rounded-xl border border-violet-100 flex items-center justify-center text-violet-600 mb-5">
                        <Compass className="w-6 h-6" />
                    </div>

                    <h3 className="text-xl font-bold text-slate-900 mb-3">Your strategic advisors</h3>
                    <p className="text-sm text-slate-500 leading-relaxed flex-1">
                        Your account manager doesn't just fix tech issues — they serve as your
                        strategic advisor. They review your metrics, identify growth opportunities,
                        and suggest plays to increase production and efficiency.
                    </p>

                    <div className="mt-5 bg-violet-50 rounded-xl border border-violet-100 px-4 py-3">
                        <span className="text-xs font-bold text-violet-600">Ongoing partnership</span>
                        <span className="text-xs text-slate-400 ml-1">beyond just onboarding</span>
                    </div>
                </motion.div>

            </div>

        </div>
    </div>
  );
};

export default WhiteGloveOnboardingSlide;
